import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Gamepad2, Trophy, Radio, CheckCircle2, PartyPopper } from 'lucide-react';
import { displayAvatar, identiconDataUri } from '../services/identicon';
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { signInWithPopup } from 'firebase/auth';
import { db, auth, provider } from '../firebase-config';
import {
  subscribeTonight, joinNight, submitEntry, subscribePlayers, POINTS,
  setPlayerTable, awardPoints, getLoadedPlaylistTracks, watchNightRequesters,
  lyricAnswerKey,
} from '../services/gameNight';
import {
  bingoCardFor, trackKeyOf, completedLines, verifyClaim,
  CARD_SIZE, BINGO_PATTERNS,
} from '../services/bingoCard';
import {
  rememberPostLoginPanel, rememberPostLoginJoin,
  consumePostLoginJoin, clearPostLoginJoin,
} from '../services/postLoginIntent';
import Fireworks from './Fireworks';
import { vibrate } from '../utils/vibration';

/**
 * Live Night panel — Muuzbox Madness Mondays for a guest, inside the LEGACY
 * player layout (AppLayoutManuf). A port of the same component in
 * muuzbox-reports-admin; both read the one night doc the gamesmaster writes,
 * so a guest here and a guest on the admin PWA see an identical room.
 *
 * The whole room flips off ONE listener: the night doc carries `liveRound`
 * (and, for bingo, the called tracks), so starting a block on the console
 * updates every phone without polling or per-round subscriptions.
 *
 * Renders nothing at all when no night is open — this panel is dead weight
 * on an ordinary evening, by design.
 */
// Fixed card geometry; the wrapper scrolls sideways when the phone is
// narrower. THE CONTAINMENT FIX: the card is `inline-block` so it shrinks to
// wrap its grid. As a plain block it took the scroll container's width while
// the 84px grid inside was wider — which is why squares sat outside the white.
// (2026-09-10: the venue ask was "less border and margins", NOT smaller
// squares — cell size stays 84px; the wrapper breaks out of the round box's
// padding instead, so more of the card is on screen.)
const CELL_PX = 84;
const GAP_PX = 6;
// Card margin around the grid — the SAME size as the gap between cells,
// so the outer ring reads as one more gap (per the venue feedback).
const PAD_PX = GAP_PX;

/** Title / artist split for the card cells (presentation only). */
const splitLabel = (label) => {
  const t = String(label || '').trim();
  const m = t.match(/^(.*?)\s+(?:—|–|-|by)\s+(.*)$/i);
  if (m && m[1].trim() && m[2].trim()) return { title: m[1].trim(), artist: m[2].trim() };
  return { title: t, artist: '' };
};

const LiveNightPanel = ({ channelName, onOpenRequests = null }) => {
  const currentUser = auth.currentUser;

  const [night, setNight] = useState(null);
  const [players, setPlayers] = useState([]);
  const [joined, setJoined] = useState(false);
  const [marks, setMarks] = useState({});
  const [answer, setAnswer] = useState('');
  const [sent, setSent] = useState(false);
  const [note, setNote] = useState('');
  const [joinTable, setJoinTable] = useState('');
  const [songList, setSongList] = useState(null);
  const [songQuery, setSongQuery] = useState('');
  const [myPick, setMyPick] = useState(null);
  // Who Requested It: the fresh request THIS guest submitted for the current
  // prompt (label + prompt stamp, so a reset/new prompt re-opens the picker).
  const [myFresh, setMyFresh] = useState(null);
  // Who Requested It — my vote this vote round, and my bonus-round guess.
  // Both keyed to the stamps so a revote (new votingAtMs) or a fresh game
  // (new promptedAtMs) re-opens the choice. Declared HERE, above the
  // no-night early return — hooks must run on every render.
  const [myVote, setMyVote] = useState(null);   // { key, votingAtMs }
  const [myGuess, setMyGuess] = useState(null); // { uid, askedAtMs }
  // Lyric Challenge — did I win the current race, and a slow clock so the
  // 10-minute countdown actually expires on screen (snapshots alone don't
  // re-render an idle phone).
  const [myLyricWin, setMyLyricWin] = useState(null); // { raceStartMs }
  const [lyricNowMs, setLyricNowMs] = useState(Date.now());
  // Showdown Final — my best-singer vote this singer-vote round.
  const [mySingerVote, setMySingerVote] = useState(null); // { uid, svAtMs }
  // Who Requested It — tonight's requesters, watched live on THIS device so
  // the line-up is on screen the whole round, console or no console.
  const [requesters, setRequesters] = useState([]);
  // The guest's PROFILE identity from users/{uid} — the avatar name they set
  // in Muuzbox ("Obalicious"), NOT the auth account name ("Oba"). The users
  // doc is the one identity every other surface (requests, rewards, profile)
  // shows, so the games must stamp and display the same name.
  const [profile, setProfile] = useState(null);

  // IDENTITY MUST NEVER OUTLIVE ITS ACCOUNT (2026-09-18, mirror of the PWA
  // copy): this used to keep the OUTGOING guest's name and photo whenever the
  // incoming account had no users/{uid} doc — the `s.exists()` branch never
  // wrote — so a new guest joined the game under the previous player's name.
  useEffect(() => {
    const uid = currentUser?.uid;
    setProfile(null);                       // drop the previous account's identity NOW
    if (!uid) return undefined;
    let dead = false;
    getDoc(doc(db, 'users', uid))
      .then((s) => {
        if (dead) return;
        setProfile(s.exists()
          ? { displayName: s.data().displayName || null, photoURL: s.data().photoURL || null }
          : { displayName: null, photoURL: null });   // no doc → fall back to the auth account
      })
      .catch(() => { if (!dead) setProfile({ displayName: null, photoURL: null }); });
    return () => { dead = true; };
  }, [currentUser?.uid]);

  const playerName = profile?.displayName || currentUser?.displayName || currentUser?.email || 'Guest';
  const playerPhoto = profile?.photoURL || currentUser?.photoURL || null;

  const flash = (m) => { setNote(m); clearTimeout(flash._t); flash._t = setTimeout(() => setNote(''), 3200); };

  useEffect(() => {
    if (!channelName) return undefined;
    return subscribeTonight(channelName, setNight);
  }, [channelName]);

  const nightId = night?.nightId || night?.id || null;
  useEffect(() => {
    if (!nightId) return undefined;
    return subscribePlayers(nightId, setPlayers);
  }, [nightId]);

  const round = night?.liveRound || null;
  const roundId = round?.roundId || null;
  const qIndex = round?.q?.index ?? null;

  useEffect(() => { setAnswer(''); setSent(false); setMyPick(null); setSongQuery(''); }, [roundId, qIndex]);
  useEffect(() => { setMyFresh(null); }, [roundId]);
  useEffect(() => { setMyVote(null); }, [round?.q?.votingAtMs]);
  useEffect(() => { setMyGuess(null); }, [round?.q?.askedAtMs]);
  useEffect(() => { setMyLyricWin(null); }, [round?.q?.raceStartMs]);
  useEffect(() => { setMySingerVote(null); }, [round?.q?.svAtMs]);
  useEffect(() => {
    if (round?.type !== 'lyric' || !round?.q?.sectionEndsAtMs) return undefined;
    const t = setInterval(() => setLyricNowMs(Date.now()), 5000);
    return () => clearInterval(t);
  }, [round?.type, round?.q?.sectionEndsAtMs]);

  // Name That Tune searches the playlist LOADED ON THE DESKTOP.
  useEffect(() => {
    if ((round?.type !== 'nametune' && round?.type !== 'whorequested' && round?.type !== 'lyric') || !night?.venueUid || !roundId) return undefined;
    let live = true;
    getLoadedPlaylistTracks(night.venueUid).then((p) => { if (live) setSongList(p); });
    return () => { live = false; };
  }, [round?.type, night?.venueUid, roundId]);

  useEffect(() => {
    if (round?.type !== 'whorequested' || !night?.venueUid) return undefined;
    // Whole Muuzbox session (6AM anchor) — matches the console's Everyone view.
    const d = new Date();
    if (d.getHours() < 6) d.setDate(d.getDate() - 1);
    d.setHours(6, 0, 0, 0);
    const since = d.getTime();
    return watchNightRequesters(night.venueUid, since, setRequesters);
  }, [round?.type, night?.venueUid, roundId]);

  // The wall the guest picks from: the console's published line-up when the
  // question carries one (identical to the big screen), else our own live
  // list. Avatars fall back to the player roster by uid, then by name.
  const requesterWall = useMemo(() => {
    const base = (round?.q?.candidates?.length ? round.q.candidates : requesters) || [];
    return base.map((c) => ({
      name: c.name,
      uid: c.uid || null,
      photo: c.photo
        || players.find((p) => p.uid && p.uid === c.uid)?.photoURL
        || players.find((p) => (p.displayName || '').toLowerCase() === c.name.toLowerCase())?.photoURL
        || null,
    }));
  }, [round?.q?.candidates, requesters, players]);

  const ntResults = useMemo(() => {
    const list = songList?.tracks || [];
    const term = songQuery.trim().toLowerCase();
    // Browsable by default — the guests scroll the SAME playlist the desktop
    // is playing from (request-page structure), search just narrows it.
    // Deliberately NO now-playing marker anywhere in this list: the whole
    // game is identifying the track by ear.
    if (term.length < 2) return list;
    return list.filter((t) =>
      (t.title || '').toLowerCase().includes(term) || (t.artist || '').toLowerCase().includes(term)
    );
  }, [songList, songQuery]);

  // Who Requested It's pick list — ALPHABETICAL by title so a specific song
  // is easy to find. Name That Tune keeps playlist order above (identifying
  // by ear is that game's point; browsing is this one's).
  const wriResults = useMemo(
    () => [...ntResults].sort((a, b) =>
      (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
      || (a.artist || '').localeCompare(b.artist || '', undefined, { sensitivity: 'base' })),
    [ntResults]
  );

  const me = useMemo(
    () => players.find((p) => p.uid === currentUser?.uid) || null,
    [players, currentUser]
  );
    useEffect(() => { setJoined(!!me); }, [me]);

  // Complete a "Join tonight" tap that was interrupted by Google sign-in.
  // On phones the sign-in can background AND relaunch the whole PWA, so the
  // doJoin continuation after signInWithPopup never runs. The tap is
  // remembered in localStorage (postLoginIntent) before the popup opens and
  // finished here once the guest is back — signed in, night open, not yet on
  // the roster. joinNight is idempotent and refuses anonymous uids, so a
  // duplicate or premature fire is harmless.
  useEffect(() => {
    if (!currentUser?.uid || currentUser.isAnonymous || !nightId || joined) return;
    const pending = consumePostLoginJoin();
    if (!pending) return;
    joinNight(nightId, {
      uid: currentUser.uid,
      displayName: playerName,
      photoURL: playerPhoto,
      tableId: pending.tableId || null,
    }).then((res) => {
      if (!res?.joined) return;
      setJoined(true);
      if (!res.already) {
        flash(res.firstEver ? `You're in — +${POINTS.join} for joining Muuzbox!` : `You're in — +${POINTS.attendance}.`);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, currentUser?.isAnonymous, nightId, joined]);

  const pool = round?.config?.pool || [];
  const card = useMemo(
    () => (round?.type === 'bingo' && currentUser?.uid && roundId ? bingoCardFor(currentUser.uid, roundId, pool) : []),
    [round?.type, currentUser?.uid, roundId, pool]
  );
  const calledKeys = useMemo(
    () => (round?.called || []).map((c) => c.key || trackKeyOf(c.label)),
    [round?.called]
  );

  // THE 5-SECOND HINT (2026-08-20, user spec): a newly called song flashes on
  // the card for 5s, then the glow fades and the square looks like any other —
  // from there it's on the guest to have been paying attention. The call
  // itself never expires (toggleCell still accepts it); only the visual cue
  // does. Joining mid-game doesn't flash the latest call — you weren't
  // listening, no hint for you.
  const [hintKey, setHintKey] = useState(null);
  const prevCalledLenRef = useRef(null);
  useEffect(() => {
    const len = calledKeys.length;
    if (prevCalledLenRef.current === null) { prevCalledLenRef.current = len; return undefined; }
    if (len <= prevCalledLenRef.current) { prevCalledLenRef.current = len; return undefined; }
    prevCalledLenRef.current = len;
    const newest = calledKeys[len - 1];
    setHintKey(null);   // a new call retires whatever hint was up
    let hideT;
    // 10s after the call before the hint flashes (2026-08-31: "delayed by a
    // further 5 seconds") — ears get a real chance before the card helps.
    // Then the usual 5-second glow. Matches the reports-admin copy.
    const showT = setTimeout(() => {
      setHintKey(newest);
      hideT = setTimeout(() => {
        setHintKey((k) => (k === newest ? null : k));
      }, 5000);
    }, 10000);
    return () => { clearTimeout(showT); if (hideT) clearTimeout(hideT); };
  }, [calledKeys]);
  const myMarks = marks[roundId] || new Set();

  const lines = useMemo(() => {
    if (!card.length) return 0;
    const marked = card.map((c, i) => (c.free || myMarks.has(i) ? c : { ...c, trackKey: '__unmarked__' }));
    return completedLines(marked, calledKeys);
  }, [card, myMarks, calledKeys]);

  const pattern = round?.config?.pattern || 'line';
    const canClaim = useMemo(() => {
    if (!card.length) return false;
    const marked = card.map((c, i) => (c.free || myMarks.has(i) ? c : { ...c, trackKey: '__unmarked__' }));
    return verifyClaim(marked, calledKeys, pattern);
  }, [card, myMarks, calledKeys, pattern]);

  // "A win is imminent" signal (2026-08-28, user spec): a line on this card
  // whose every track the host has ALREADY called, even if the player has not
  // yet toggled every square of it. If any such line exists, the player can
  // mark those squares and claim BINGO right now — so the BINGO button pulses
  // orange to draw their eye to the card. `completedLines` here runs on the raw
  // card (vs. `marked` above, which the player must have tapped), so this fires
  // a beat BEFORE `canClaim` does: the nudge, not the green light.
  const winAlert = useMemo(() => {
    if (!joined || !card.length) return false;
    return completedLines(card, calledKeys) > 0;
  }, [card, calledKeys, joined]);

  // ── Win celebration — fireworks on EVERY announced winner ─────────────
  // Ported from the reports-admin copy of this panel so a launcher guest and
  // a PWA guest see the same show. Any round state that announces a winner
  // BY NAME lights the fireworks: bingo (verified claim), Name That Tune
  // (reveal), Who Requested It (the picker), Lyric Challenge (first correct
  // pick) and the Showdown Final (champion). Keyed by round|game|who(+stamp)
  // so each NEW winner fires a fresh volley, while unrelated snapshot churn
  // (vote bars, bingo calls, the gamesmaster's verdict) can't restart the
  // show mid-burst. Declared above the no-night early return — hooks run on
  // every render.
  const celebration = useMemo(() => {
    if (!round) return null;
    const rid = round.roundId || '';
    const q = round.q || null;
    if (round.type === 'bingo' && round.bingoWinner?.name) {
      return {
        key: `${rid}|bingo|${round.bingoWinner.name}|${round.bingoWinner.atMs || ''}`,
        name: round.bingoWinner.name,
        kicker: '🎟️ BINGO!',
        sub: 'verified — card checked!',
      };
    }
    if (round.type === 'nametune' && q?.status === 'revealed' && q.winner) {
      return { key: `${rid}|ntt|${q.winner}|${q.index ?? ''}`, name: q.winner, kicker: '🏆 WE HAVE A WINNER', sub: 'found it first!' };
    }
    if (round.type === 'whorequested' && q?.status === 'revealed' && q.answer) {
      return { key: `${rid}|wri|${q.answer}|${q.promptedAtMs || ''}`, name: q.answer, kicker: '🎵 IT WAS…', sub: 'picked the winning track' };
    }
    if (round.type === 'lyric' && q?.status === 'won' && q.winner) {
      return { key: `${rid}|lyric|${q.winner}|${q.answer || ''}`, name: q.winner, kicker: '⚡ FIRST CORRECT PICK', sub: q.answer || '' };
    }
    if (round.type === 'showdown' && q?.status === 'revealed' && q.champion?.name) {
      return { key: `${rid}|sd|${q.champion.name}|${q.svAtMs || ''}`, name: q.champion.name, kicker: '👑 KARAOKE CHAMPION', sub: 'takes the Showdown Final!' };
    }
    return null;
  }, [round]);
  const [celebrationKey, setCelebrationKey] = useState(null);
  // VENUE-HOP RESIDUE GUARD (2026-09-16 live spec, mirror of the PWA copy):
  // a guest can play venue A then scan into venue B the same evening — a
  // winner announcement already sitting on a night doc at ENTRY is residue
  // from a game they weren't in and must never render or buzz.
  // Prime-then-react: the first celebration derived after entry (mount or
  // channel switch) is consumed silently; only keys appearing AFTER fire.
  const entryCelKeyRef = useRef(); // undefined = not yet primed for this entry
  const [suppressedCelKey, setSuppressedCelKey] = useState(null);
  useEffect(() => {
    entryCelKeyRef.current = undefined;
    setSuppressedCelKey(null);
    setCelebrationKey(null);
  }, [channelName]);
  useEffect(() => {
    if (!celebration) {
      if (entryCelKeyRef.current === undefined) entryCelKeyRef.current = null;
      setCelebrationKey(null);
      return undefined;
    }
    if (entryCelKeyRef.current === undefined) {
      entryCelKeyRef.current = celebration.key;
      setSuppressedCelKey(celebration.key);
      setCelebrationKey(null);
      return undefined;
    }
    if (celebration.key === entryCelKeyRef.current) return undefined; // still the residue
    setSuppressedCelKey(null);
    setCelebrationKey(celebration.key);
    // EVERY player's phone buzzes with the fireworks (2026-08-31 spec) — a
    // celebratory burst as they light, an echo mid-show; not a 9s drone.
    // (Android buzzes; iOS Safari has no Vibration API.)
    vibrate([400, 150, 400, 150, 700]);
    const echo = setTimeout(() => vibrate([250, 120, 250]), 2600);
    const t = setTimeout(() => setCelebrationKey(null), 9500);
    return () => { clearTimeout(t); clearTimeout(echo); };
  }, [celebration?.key]);

  // The confirmed winner is ME — the celebration banner says so out loud.
  const iWon = !!celebration
    && celebration.name.trim().toLowerCase() === (me?.nickname || playerName).trim().toLowerCase();

  if (!channelName || !night || night.status === 'closed') {
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-8 text-center">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'linear-gradient(135deg,#E8007D,#7C3AED)' }}>
          <Gamepad2 size={28} className="text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Muuzbox Madness Mondays</h2>
        <p className="text-base text-white/50 max-w-md mx-auto">
          {!channelName
            ? 'Pick the venue you’re in to join the game night.'
            : night?.status === 'closed'
              ? 'Tonight’s game night has wrapped. Same time next Monday — 19:30.'
              : 'No game night running right now. Mondays 19:30 — music bingo, song requests, and the Muuzbox Showdown.'}
        </p>
      </div>
    );
  }

  const doJoin = async () => {
    if (!currentUser?.uid || currentUser.isAnonymous) {
      flash('Sign in with Google to play tonight…');
      // Anonymous (or signed-out) guests are routed to the Google sign-in
      // BEFORE they can join. This host's games are Google-accounts-only —
      // an anonymous session can never join the roster.
      //
      // On a phone the sign-in backgrounds the PWA and the OS may kill and
      // relaunch it — this continuation then NEVER runs and no error is
      // thrown. Remember both the destination (Games) and the tap itself in
      // localStorage first, so the relaunched app re-opens Games
      // (AppLayoutManuf) and finishes this join (the mount effect above).
      rememberPostLoginPanel('games', channelName);
      rememberPostLoginJoin(joinTable || null);
      try {
        const cred = await signInWithPopup(auth, provider);
        const u = cred.user;
        // Popup completed IN-PAGE, so this continuation owns the join —
        // clear the relaunch fallback BEFORE joining, or the auto-join
        // effect (also woken by the uid change) consumes it concurrently
        // and both call joinNight at once. The transactional award stops
        // the double-pay, but there's no reason to race at all.
        clearPostLoginJoin();
        // Re-render picks up the new identity — join with the Google user.
        const res = await joinNight(nightId, {
          uid: u.uid,
          displayName: u.displayName || u.email || 'Guest',
          photoURL: u.photoURL || null,
          tableId: joinTable || null,
        });
        setJoined(true);
        flash(res.firstEver ? `You're in — +${POINTS.join} for joining Muuzbox!` : `You're in — +${POINTS.attendance}.`);
      } catch (e) {
        // Reached = still in-page (cancelled/failed): the tap is void.
        clearPostLoginJoin();
        flash(e?.code === 'auth/popup-closed-by-user' ? 'Google sign-in cancelled — join needs a Google account.' : `Could not sign in: ${e?.message || 'unknown'}`);
      }
      return;
    }
    const res = await joinNight(nightId, {
      uid: currentUser.uid,
      displayName: playerName,
      photoURL: playerPhoto,
      tableId: joinTable || null,
    });
    setJoined(true);
    flash(res.firstEver ? `You're in — +${POINTS.join} for joining Muuzbox!` : `You're in — +${POINTS.attendance}.`);
  };

  const toggleCell = (i) => {
    if (card[i]?.free) return;
    if (!calledKeys.includes(card[i].trackKey)) { flash('That one hasn’t been played yet.'); return; }
    setMarks((old) => {
      const set = new Set(old[roundId] || []);
      if (set.has(i)) set.delete(i); else set.add(i);
      return { ...old, [roundId]: set };
    });
  };

  const claimBingo = async () => {
    // Google-only for claims too: an anonymous session must never put a
    // claim (or a name) on the host's board.
    if (!currentUser?.uid || currentUser.isAnonymous) {
      flash('Sign in with Google to claim bingo…');
      // Same phone-relaunch hazard as doJoin: make sure a killed PWA comes
      // back to the Games panel after the sign-in completes.
      rememberPostLoginPanel('games', channelName);
      try {
        await signInWithPopup(auth, provider);
      } catch (e) {
        flash(e?.code === 'auth/popup-closed-by-user' ? 'Google sign-in cancelled — claiming needs a Google account.' : `Could not sign in: ${e?.message || 'unknown'}`);
      }
      return;
    }
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      displayName: playerName,
      answer: `${BINGO_PATTERNS[pattern]?.label || pattern} · ${lines} line(s)`,
      kind: 'bingo',
    });
    setSent(true);
    flash('BINGO claimed — the host is checking your card!');
  };

  // (sendAnswer removed 2026-08-19 — WRI guesses now go through guessWho;
  //  no remaining caller in this copy of the panel.)

  const pickChoice = async (idx) => {
    const text = round?.q?.choices?.[idx];
    if (text == null) return;
    setMyPick({ title: text, index: idx });
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: text,
      choiceIndex: idx,
      kind: 'answer',
      qIndex,
    });
    setSent(true);
    flash('Locked in — fingers crossed!');
  };

  /** One key per SONG — must mirror the gamesmaster console's trackKey. */
  const wriKey = (title, artist) =>
    `${String(title || '').trim().toLowerCase()}|${String(artist || '').trim().toLowerCase()}`;

  /** Who Requested It — submit this guest's SECRET pick for the prompt. */
  const sendFreshRequest = async (t) => {
    if (!currentUser?.uid || !round?.q?.promptedAtMs) return;
    const label = `${t.title}${t.artist ? ` — ${t.artist}` : ''}`;
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: label,
      trackId: t.id || null,
      coverart: t.coverart || null,
      kind: 'freshrequest',
      qIndex: 'fresh',
    });
    setMyFresh({ label, key: wriKey(t.title, t.artist), promptedAtMs: round.q.promptedAtMs });
    flash('Pick in — keep it secret!');
  };

  // The pick is mine → voting for it is BLOCKED (even if someone else chose
  // the same song — you never vote for your own track).
  const isMyOwnTrack = (b) =>
    !!(myFresh && myFresh.promptedAtMs === round?.q?.promptedAtMs && myFresh.key === b.key);

  const voteForTrack = async (b) => {
    if (!currentUser?.uid || !round?.q?.votingAtMs) return;
    if (isMyOwnTrack(b)) { flash('That one’s YOUR pick — vote for someone else’s. 🤫'); return; }
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: b.key,
      kind: 'trackvote',
      qIndex: `vote${round.q.voteRound || 1}`,
    });
    setMyVote({ key: b.key, votingAtMs: round.q.votingAtMs });
    flash('Vote in!');
  };

  /** Lyric Challenge — hash-gated pick: only a CORRECT tap ever submits. */
  const pickLyricTrack = async (t) => {
    if (!currentUser?.uid || !round?.q?.raceStartMs || !round?.q?.answerKey) return;
    const key = wriKey(t.title, t.artist);
    if (lyricAnswerKey(round.q.raceStartMs, key) !== round.q.answerKey) {
      flash('Not it — keep trying! 🎯');
      return;
    }
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: `${t.title}${t.artist ? ` — ${t.artist}` : ''}`,
      trackId: t.id || null,
      kind: 'lyricpick',
      qIndex: `lyr${round.q.raceStartMs}`,
    });
    setMyLyricWin({ raceStartMs: round.q.raceStartMs });
    flash('🎉 You found it!');
  };

  /** Showdown Final — vote the best karaoke singer (never yourself). */
  const voteSinger = async (singer) => {
    if (!currentUser?.uid || !round?.q?.svAtMs || !singer?.uid) return;
    if (singer.uid === currentUser.uid) { flash('You can’t vote for yourself, superstar. 🤭'); return; }
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: singer.uid,
      kind: 'singervote',
      qIndex: 'sv',
    });
    setMySingerVote({ uid: singer.uid, svAtMs: round.q.svAtMs });
    flash('Vote in!');
  };

  const guessWho = async (c) => {
    if (!currentUser?.uid || !round?.q?.askedAtMs || !c?.uid) return;
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: c.uid,
      kind: 'whoguess',
      qIndex: 'guess',
    });
    setMyGuess({ uid: c.uid, askedAtMs: round.q.askedAtMs });
    flash('Guess locked in!');
  };

  const pickSong = async (t) => {
    if (qIndex === null || qIndex === undefined) return;   // browsing — no question open yet
    setMyPick(t);
    await submitEntry(nightId, roundId, {
      uid: currentUser.uid,
      teamId: me?.teamId || null,
      tableId: me?.tableId || null,
      displayName: playerName,
      answer: `${t.title}${t.artist ? ` — ${t.artist}` : ''}`,
      trackId: t.id,
      kind: 'answer',
      qIndex,
    });
    setSent(true);
    flash('Answer locked in — first correct pick takes it!');
  };

  return (
    <div className="mb-6 rounded-2xl border border-purple-500/30 bg-gradient-to-b from-purple-500/10 to-transparent p-5 text-white">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <Gamepad2 size={22} className="text-white" />
          </div>
          <div>
            <div className="text-xl font-bold leading-tight">
              {night.status === 'live' ? 'Tonight is LIVE' : 'Tonight'}
              {night.theme ? <span className="text-white/50 font-normal"> · {night.theme}</span> : null}
            </div>
            <div className="text-base text-white/50">
              {channelName}{night.weekNumber ? ` · Week ${night.weekNumber}` : ''}
            </div>
          </div>
        </div>
        {me ? (
          <div className="text-right">
            <div className="text-2xl font-bold font-mono text-purple-300">{me.points || 0}</div>
            <div className="text-base text-white/40">your points</div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {(night.tables || []).length > 0 && (
              <select
                value={joinTable}
                onChange={(e) => setJoinTable(e.target.value)}
                className="text-base bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white focus:outline-none"
                style={{ colorScheme: 'dark' }}
              >
                <option value="">Your table…</option>
                {(night.tables || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            <button onClick={doJoin}
              className="text-base bg-purple-600 hover:bg-purple-500 text-white px-5 py-2.5 rounded-lg font-semibold transition">
              Join tonight
            </button>
          </div>
        )}
      </div>

      {me && (night.tables || []).length > 0 && !me.tableId && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-base text-white/60">Pick your table:</span>
          {(night.tables || []).map((t) => (
            <button key={t}
              onClick={async () => { await setPlayerTable(nightId, currentUser.uid, t); flash(`You're on ${t}.`); }}
              className="text-base px-3.5 py-2 rounded-full border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 transition">
              {t}
            </button>
          ))}
        </div>
      )}

      {note && <div className="text-base text-green-400 mb-3">{note}</div>}

      {/* Win celebration — the winner's name, huge, over a fireworks volley.
          Sits ABOVE the round box so the card / answers below stay usable. */}
      {celebration && celebration.key !== suppressedCelKey && (
        <div
          className="rounded-2xl mb-3 px-4 py-4 text-center border border-amber-400/50"
          style={{
            background: 'linear-gradient(135deg, rgba(232,0,125,.22), rgba(124,58,237,.22))',
            boxShadow: '0 0 34px rgba(232,0,125,.25)',
          }}
        >
          <div className="text-base font-bold tracking-widest text-amber-300 uppercase">{celebration.kicker}</div>
          {iWon ? (
            <div
              className="text-5xl font-extrabold leading-tight"
              style={{
                fontFamily: '"Syne", sans-serif',
                background: 'linear-gradient(135deg, #FFD700, #FF8C00)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              🎉 YOU WON!
            </div>
          ) : null}
          <div className={`${iWon ? 'text-2xl' : 'text-4xl'} font-extrabold leading-tight text-white`} style={{ fontFamily: '"Syne", sans-serif' }}>
            {celebration.name}
          </div>
          {celebration.sub ? <div className="text-base text-white/70 mt-0.5">{celebration.sub}</div> : null}
        </div>
      )}
      {celebrationKey && <Fireworks fireKey={celebrationKey} />}

            {!me || !round ? (
        <div className="text-base text-white/50 py-3">
          {joined
            ? 'Waiting for the next round — keep your phone handy.'
            : 'Join to play along and score on the leaderboard.'}
        </div>
      ) : (
        <div
          // Keyed per STAGE: every round / question / phase change remounts
          // this card, replaying the fade-in + title bulge (2026-08-31 spec).
          // BINGO BREAKS THE MOULD (2026-09-11 user spec: "total redesign of
          // how the cards are presented"): no boxed container at all. The
          // card IS the surface — a 100vw breakout escapes every ancestor's
          // padding (round box, panel, page) so nothing frames it. All other
          // round types keep the boxed look.
          key={`${roundId}|${round.type}|${round.q?.status || ''}|${qIndex ?? ''}`}
          className={round.type === 'bingo'
            ? 'mbx-stage-enter'
            : 'rounded-xl bg-black/25 border border-white/10 p-4 mbx-stage-enter'}
          style={round.type === 'bingo'
            ? { width: '100vw', marginLeft: 'calc(50% - 50vw)' }
            : undefined}
        >
          {round.type !== 'bingo' && (
          <div className="flex items-center gap-2 mb-3">
            <Radio size={17} className={round.status === 'scoring' ? 'text-amber-400' : 'text-green-400'} />
            <span className="text-base font-semibold mbx-bulge">{round.title}</span>
            <span className="text-base text-white/40">
              {round.status === 'scoring' ? '· answers locked' : '· live now'}
            </span>
          </div>
          )}

          {/* BINGO */}
          {round.type === 'bingo' && (
            card.length === 0 ? (
              <p className="text-base text-white/50 px-4 py-3">The host is setting up the card…</p>
            ) : (
              <>
                {/* THE CARD AS THE SURFACE (2026-09-11 redesign): the white
                    paper runs edge-to-edge with square sides — no rounded
                    box, no border, no framing container. Same 84px squares
                    as always (content untouched); the wrapper still scrolls
                    sideways when the grid outgrows the screen. */}
                <div style={{ margin: '0 0 0.75rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <div
                    style={{
                      display: 'inline-block',
                      minWidth: '100%',
                      background: '#fff',
                      padding: PAD_PX,
                      // Cells pin margin/padding inline — the misalignment was
                      // the global `button { margin-left }` rule, not sizing.
                      boxShadow: '0 8px 24px rgba(0,0,0,.38)',
                    }}>
                    <div className="flex items-center justify-between gap-3 pb-2"
                      style={{ width: '100%' }}>
                      <span style={{ fontFamily: '"Syne", sans-serif', fontWeight: 800, fontSize: 15, color: '#111', whiteSpace: 'nowrap' }}>
                        MUSIC BINGO
                      </span>
                      <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 13, color: '#666', whiteSpace: 'nowrap' }}>
                        {channelName}{round.status === 'scoring' ? ' · LOCKED' : ''}
                      </span>
                    </div>
                    <div
                      className="grid"
                      style={{
                        gap: GAP_PX,
                        gridTemplateColumns: `repeat(${CARD_SIZE}, ${CELL_PX}px)`,
                        gridTemplateRows: `repeat(${CARD_SIZE}, ${CELL_PX}px)`,
                      }}
                    >
                  {card.map((cell, i) => {
                    const isMarked = cell.free || myMarks.has(i);
                    // The ONLY visual tell for a called-but-unmarked square is
                    // the 5-second hint flash on the newest call. After it
                    // fades, called squares look identical to uncalled ones —
                    // paying attention is the game.
                    const isHint = !isMarked && hintKey && cell.trackKey === hintKey;
                    return (
                      <button
                        key={i}
                        onClick={() => toggleCell(i)}
                        disabled={round.status === 'scoring'}
                        className="rounded-lg p-1.5 flex flex-col items-center justify-center text-center transition overflow-hidden"
                        style={{
                          // Inline, so the app's `box-sizing: inherit` reset
                          // can't push the border outside the 84px track and
                          // bleed the last column into the card margin.
                          boxSizing: 'border-box',
                          // ManufMuuzbox.scss has a blanket `button { margin-left: .5rem }`
                          // under the app root — on a grid of 100%-wide cells that pushes
                          // every column right and shoves the last one over the card edge.
                          // Inline zero beats it without touching the global rule.
                          margin: 0,
                          // Same global rule also forces padding .5rem 1rem,
                          // beating the Tailwind p-1.5 class — pin the intended
                          // 6px inline so titles keep their full width.
                          padding: 6,
                          width: '100%',
                          height: '100%',
                          border: isMarked ? '1px solid transparent'
                            : isHint ? '1.5px solid #E8007D' : '1px solid #333',
                          background: isMarked
                            ? 'linear-gradient(135deg,#E8007D 0%,#7C3AED 100%)'
                            : isHint ? 'rgba(232,0,125,.12)' : '#fff',
                          boxShadow: isHint ? '0 0 12px rgba(232,0,125,.45)' : 'none',
                          color: isMarked ? '#fff' : '#111',
                        }}
                      >
                        {(() => {
                          const { title, artist } = splitLabel(cell.label);
                          return (
                            <>
                              <span style={{
                                fontWeight: 700, fontSize: 12, lineHeight: 1.15, width: '100%',
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                overflow: 'hidden', overflowWrap: 'anywhere',
                              }}>{title}</span>
                              {artist ? (
                                <span style={{
                                  fontSize: 10.5, lineHeight: 1.15, marginTop: 2, width: '100%',
                                  color: isMarked ? 'rgba(255,255,255,.85)' : '#555',
                                  display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden', overflowWrap: 'anywhere',
                                }}>{artist}</span>
                              ) : null}
                            </>
                          );
                        })()}
                      </button>
                    );
                  })}
                    </div>
                  </div>
                </div>
                {/* Full-bleed section supplies no padding of its own — this
                    strip pads itself so text/button clear the screen edge. */}
                <div className="flex items-center justify-between flex-wrap gap-3 px-4 pb-2">
                  <span className="text-base text-white/50">
                    {lines} line{lines === 1 ? '' : 's'} · needs {BINGO_PATTERNS[pattern]?.label || pattern}
                    {' · '}{(round.called || []).length} played
                  </span>
                  <button
                    onClick={claimBingo}
                    disabled={!canClaim || sent || round.status === 'scoring'}
                    className="flex items-center gap-2 text-base px-5 py-2.5 rounded-lg font-bold transition"
                    // INLINE colour (2026-08-28 user spec, ported from the
                    // reports-admin copy): the bright orange must survive
                    // every theme layer — ManufMuuzbox.scss's blanket
                    // `.Manuf button { background: … }` shorthand wipes any
                    // class-based gradient, so no class rule can be trusted
                    // here. Same #FF9D00→#FF6A00 as the PWA.
                    style={
                      sent || !joined
                        ? { border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.35)', background: 'transparent' }
                        : winAlert
                          ? {
                              background: 'linear-gradient(135deg, #FF9D00, #FF6A00)',
                              color: '#1a1000',
                              border: 'none',
                              animation: 'bingoClaimPulse 1.15s ease-in-out infinite',
                            }
                          : { border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.35)', background: 'transparent' }
                    }
                  >
                    <PartyPopper size={18} /> {sent ? 'Claimed' : 'BINGO!'}
                  </button>
                </div>
              </>
            )
          )}

          {/* Name That Tune — race to FIND it in the desktop's loaded playlist */}
          {round.type === 'nametune' && (
            round.q?.status === 'revealed' ? (
              <div className="text-base py-1">
                {round.q.winner && (
                  <div className="text-amber-300 font-bold mb-1">🏆 {round.q.winner} found it first!</div>
                )}
                <span className="text-white/50">Q{(round.q.index ?? 0) + 1} answer: </span>
                <b className="text-green-400">{round.q.answer}</b>
              </div>
            ) : round.q && sent ? (
                <div className="text-base py-2">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle2 size={18} /> Q{(round.q.index ?? 0) + 1} answer in:&nbsp;
                    <b>{myPick ? myPick.title : answer}</b>
                  </div>
                  <button onClick={() => setSent(false)}
                    className="text-base text-white/50 underline mt-1.5">
                    Change answer
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-base text-white/70 mb-2">
                    {round.q
                      ? <>🎧 Question {(round.q.index ?? 0) + 1} — what’s playing? Find it in <b>tonight’s playlist</b>. First correct pick wins +{POINTS.correctAnswer}!</>
                      : <>🎧 This is <b>tonight’s playlist</b> — the one the desk is playing from. Browse it now; when the host starts a question, first correct tap wins!</>}
                  </div>
                  <input
                    value={songQuery}
                    onChange={(e) => setSongQuery(e.target.value)}
                    disabled={round.status === 'scoring' || !joined}
                    placeholder={
                      songList === null ? 'Loading tonight’s playlist…'
                        : songList.tracks.length === 0 ? 'Playlist not available yet — hang tight'
                        : `Search ${songList.tracks.length} songs…`
                    }
                    className="w-full bg-white border border-white/15 rounded-lg px-4 py-3 placeholder-gray-500 focus:outline-none focus:border-purple-500/60 disabled:opacity-40" style={{ fontSize: 17, color: "#111", background: "#fff" }}
                  />
                  {songQuery.trim().length >= 2 && ntResults.length === 0 && songList?.tracks?.length > 0 && (
                    <p className="text-base text-white/40 mt-2">No matches — try fewer letters.</p>
                  )}
                  {ntResults.length > 0 && (
                    <div className="space-y-1.5 mt-2 max-h-96 overflow-y-auto">
                      {ntResults.slice(0, 400).map((t) => (
                        <button key={t.id} onClick={() => pickSong(t)}
                          disabled={!joined || round.status === 'scoring' || !round.q}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-purple-400/60 hover:bg-purple-500/10 transition text-left disabled:opacity-40">
                          {t.coverart ? (
                            <img src={t.coverart} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center flex-shrink-0">🎵</div>
                          )}
                          <span className="flex-1 min-w-0 text-base truncate">
                            <b>{t.title}</b>{t.artist ? <span className="text-white/50"> — {t.artist}</span> : null}
                          </span>
                          <span className="text-base text-purple-300 flex-shrink-0">This one!</span>
                        </button>
                      ))}
                      {ntResults.length > 400 && (
                        <p className="text-base text-white/40 py-1.5 text-center">
                          {ntResults.length - 400} more — type to narrow it down
                        </p>
                      )}
                    </div>
                  )}
                </>
            )
          )}

          {/* Who Requested It? — pick → room vote → top track plays → guess who picked it */}
          {round.type === 'whorequested' && (
            round.q?.status === 'revealed' ? (
              <div className="text-base py-1">
                <div className="text-amber-300 font-bold mb-1">
                  🏆 {round.q.answer} picked the winning track{round.q.winner?.title ? ` — “${round.q.winner.title}”` : ''}!
                </div>
                {(round.q.correctGuessers || []).length > 0 && (
                  <div className="text-green-400">
                    ✓ Guessed right (+{POINTS.wriGuessWho}): {(round.q.correctGuessers || []).join(', ')}
                  </div>
                )}
                {myGuess && (round.q.winnerPickers || []).some((p) => p.uid === myGuess.uid) && (
                  <div className="text-green-400 font-bold mt-1">You guessed it — +{POINTS.wriGuessWho}!</div>
                )}
                {round.q.gmVerdict && (
                  <div className={`mt-1 ${round.q.gmVerdict === 'liked' ? 'text-emerald-300' : 'text-red-300'}`}>
                    {round.q.gmVerdict === 'liked'
                      ? `👍 The gamesmaster rates that pick — +${POINTS.gmSongVerdict}!`
                      : `👎 The gamesmaster was not impressed — −${POINTS.gmSongVerdict}.`}
                  </div>
                )}
              </div>
            ) : round.q?.status === 'prompting' ? (
              (round.q.mode === 'ingame' && !joined) ? (
                <p className="text-base text-white/50">The registered players are picking their tracks…</p>
              ) : myFresh && myFresh.promptedAtMs === round.q.promptedAtMs ? (
                <div className="flex items-center gap-2 text-base text-green-400 py-2">
                  <CheckCircle2 size={18} /> Pick in: <b>{myFresh.label}</b> — keep it secret till the vote.
                </div>
              ) : (
                <>
                  <div className="text-lg text-white/80 mb-2">
                    🎤 <b>Secretly pick a track</b> from tonight’s playlist. Every pick
                    goes on the big screen and the room votes — win the vote and it
                    plays for +{POINTS.wriTopTrack}.
                    <span className="text-white/50"> The gamesmaster also rates the winning pick (+{POINTS.gmSongVerdict} / −{POINTS.gmSongVerdict}).</span>
                  </div>
                  <input
                    value={songQuery}
                    onChange={(e) => setSongQuery(e.target.value)}
                    placeholder={songList === null ? 'Loading tonight’s playlist…' : `Search ${songList?.tracks?.length || 0} songs…`}
                    className="w-full bg-white border border-white/15 rounded-lg px-4 py-3 placeholder-gray-500 focus:outline-none focus:border-purple-500/60" style={{ fontSize: 17, color: "#111", background: "#fff" }}
                  />
                  {wriResults.length > 0 && (
                    <div className="space-y-1.5 mt-2 max-h-96 overflow-y-auto">
                      {wriResults.slice(0, 400).map((t) => (
                        <button key={t.id} onClick={() => sendFreshRequest(t)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 hover:border-pink-400/60 hover:bg-pink-500/10 transition text-left overflow-hidden">
                          {t.coverart ? (
                            <img src={t.coverart} alt="" className="w-11 h-11 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-11 h-11 rounded bg-white/10 flex items-center justify-center flex-shrink-0">🎵</div>
                          )}
                          <span className="flex-1 min-w-0 text-lg truncate">
                            <b>{t.title}</b>{t.artist ? <span className="text-white/50"> — {t.artist}</span> : null}
                          </span>
                          <span className="text-lg text-pink-300 flex-shrink-0 whitespace-nowrap">Pick it</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )
            ) : round.q?.status === 'voting' ? (
              <>
                <div className="text-base text-white/80 mb-2">
                  🗳 {round.q.voteRound === 2
                    ? <b>It’s a TIE — vote again between these!</b>
                    : <b>Vote for the track the room should hear.</b>}
                  <span className="text-white/50"> Winner plays — and its picker banks +{POINTS.wriTopTrack}. You can’t vote for your own pick.</span>
                </div>
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {(round.q.ballot || []).map((b) => {
                    const mine = isMyOwnTrack(b);
                    const voted = myVote && myVote.votingAtMs === round.q.votingAtMs && myVote.key === b.key;
                    return (
                      <button key={b.key} onClick={() => voteForTrack(b)} disabled={mine}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition text-left ${
                          voted ? 'border-green-400/70 bg-green-500/10'
                            : mine ? 'border-white/10 bg-white/[0.03] opacity-60'
                              : 'border-white/10 bg-white/5 hover:border-pink-400/60 hover:bg-pink-500/10'}`}>
                        {b.coverart ? (
                          <img src={b.coverart} alt="" className="w-11 h-11 rounded object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-11 h-11 rounded bg-white/10 flex items-center justify-center flex-shrink-0">🎵</div>
                        )}
                        <span className="flex-1 min-w-0 text-base truncate">
                          <b>{b.title}</b>{b.artist ? <span className="text-white/50"> — {b.artist}</span> : null}
                        </span>
                        <span className={`text-base flex-shrink-0 ${voted ? 'text-green-400' : mine ? 'text-white/40' : 'text-pink-300'}`}>
                          {voted ? '✓ your vote' : mine ? 'your pick 🤫' : 'Vote'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {myVote && myVote.votingAtMs === round.q.votingAtMs && (
                  <p className="text-base text-white/50 mt-2">Vote in — tap another track to change it before voting closes.</p>
                )}
              </>
            ) : round.q?.status === 'guessing' ? (
              myFresh && myFresh.promptedAtMs === round.q.promptedAtMs && myFresh.key === round.q.winner?.key ? (
                <div className="text-base py-2">
                  <div className="text-amber-300 font-bold">🏆 Your track won the vote — +{POINTS.wriTopTrack}!</div>
                  <p className="text-white/50 mt-1">Sit tight and look innocent while they guess who picked it…</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    {round.q.winner?.coverart ? (
                      <img src={round.q.winner.coverart} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center flex-shrink-0">🎵</div>
                    )}
                    <div className="min-w-0">
                      <div className="text-base"><b>{round.q.winner?.title}</b>{round.q.winner?.artist ? <span className="text-white/50"> — {round.q.winner.artist}</span> : null}</div>
                      <div className="text-base text-white/50">won the vote and is playing now · <b>who picked it?</b> +{POINTS.wriGuessWho}</div>
                    </div>
                  </div>
                  {requesterWall.length > 0 ? (
                    <div className="flex flex-wrap gap-2.5">
                      {requesterWall.map((c) => {
                        const guessed = myGuess && myGuess.askedAtMs === round.q.askedAtMs && myGuess.uid === c.uid;
                        return (
                          <button key={c.uid || c.name} onClick={() => guessWho(c)}
                            className={`flex flex-col items-center gap-1.5 w-24 py-2.5 rounded-xl border transition ${
                              guessed ? 'border-green-400/70 bg-green-500/10' : 'border-white/15 hover:border-pink-400/60 hover:bg-pink-500/10'}`}>
                            <img
                              src={displayAvatar(c.photo, c.uid ? c.uid.substring(0, 6) : c.name)}
                              alt=""
                              className="w-14 h-14 rounded-full object-cover"
                              onError={(ev) => { ev.currentTarget.src = identiconDataUri(c.uid ? c.uid.substring(0, 6) : c.name); }}
                            />
                            <span className="text-base text-white/85 w-full text-center px-1 truncate">{c.name}</span>
                            {guessed && <span className="text-[13px] text-green-400">✓ your guess</span>}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-base text-white/50">Waiting for the line-up…</p>
                  )}
                </>
              )
            ) : round.q?.status === 'asking' ? (
              // Legacy status from a console still running the pre-vote build.
              // Mixed versions mid-night otherwise fail SILENTLY on phones.
              <p className="text-base text-amber-300">
                ⚠ The gamesmaster console is running an older version of this
                game — gamesmaster: refresh the console page, hit Reset game,
                and run the round again.
              </p>
            ) : (
              <p className="text-base text-white/50">The gamesmaster is setting up — hold on…</p>
            )
          )}

          {/* Lyric Challenge — spot the song the lyric came from */}
          {round.type === 'lyric' && (
            (round.q?.sectionEndsAtMs && lyricNowMs > round.q.sectionEndsAtMs) ? (
              <div className="text-base py-2">
                <div className="text-amber-300 font-bold">⏰ Lyric Challenge finished!</div>
                {round.q?.status === 'won' && round.q.winner && (
                  <div className="text-white/70 mt-1">Last winner: <b>{round.q.winner}</b> — {round.q.answer}</div>
                )}
                <p className="text-white/50 mt-1">The gamesmaster is lining up the next game…</p>
              </div>
            ) : round.q?.status === 'won' ? (
              <div className="text-base py-1">
                <div className="text-amber-300 font-bold mb-1">🏆 {round.q.winner} got it first!</div>
                <span className="text-white/50">The song: </span>
                <b className="text-green-400">{round.q.answer}</b>
                <p className="text-white/50 mt-1">Next lyric coming up — stay sharp…</p>
              </div>
            ) : round.q?.status === 'racing' ? (
              myLyricWin?.raceStartMs === round.q.raceStartMs ? (
                <div className="flex items-center gap-2 text-base text-green-400 py-2">
                  <CheckCircle2 size={18} /> You found it — waiting for the crown!
                </div>
              ) : (
                <>
                  <div className="mb-3 px-4 py-3 rounded-xl border border-amber-500/40 bg-amber-500/10">
                    <div className="text-base text-white/60 mb-1">🎤 Which song is this lyric from?</div>
                    {/* pre-line: the gamesmaster can send up to 4 lines, newline-joined */}
                    <div className="text-lg font-bold italic" style={{ whiteSpace: 'pre-line' }}>“{round.q.lyric}”</div>
                  </div>
                  <div className="text-base text-white/50 mb-2">
                    First correct pick wins +{POINTS.correctAnswer} — guess as
                    many times as you like!
                  </div>
                  <input
                    value={songQuery}
                    onChange={(e) => setSongQuery(e.target.value)}
                    placeholder={songList === null ? 'Loading tonight’s playlist…' : `Search ${songList?.tracks?.length || 0} songs…`}
                    className="w-full bg-white border border-white/15 rounded-lg px-4 py-3 placeholder-gray-500 focus:outline-none focus:border-purple-500/60" style={{ fontSize: 17, color: "#111", background: "#fff" }}
                  />
                  {wriResults.length > 0 && (
                    <div className="space-y-1.5 mt-2 max-h-96 overflow-y-auto">
                      {wriResults.slice(0, 400).map((t) => (
                        <button key={t.id} onClick={() => pickLyricTrack(t)}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-amber-400/60 hover:bg-amber-500/10 transition text-left">
                          {t.coverart ? (
                            <img src={t.coverart} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center flex-shrink-0">🎵</div>
                          )}
                          <span className="flex-1 min-w-0 text-base truncate">
                            <b>{t.title}</b>{t.artist ? <span className="text-white/50"> — {t.artist}</span> : null}
                          </span>
                          <span className="text-base text-amber-300 flex-shrink-0">This one!</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )
            ) : (
              <p className="text-base text-white/50">Get ready — the host is picking a lyric…</p>
            )
          )}

          {/* Mood capture */}
          {round.type === 'mood' && (
            sent ? (
              <div className="flex items-center gap-2 text-base text-green-400 py-2">
                <CheckCircle2 size={18} /> Mood sent — watch it land on the big screen.
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {['Energetic', 'Party', 'Chill', 'Romantic'].map((m) => (
                  <button
                    key={m}
                    onClick={async () => {
                      await submitEntry(nightId, roundId, {
                        uid: currentUser.uid, teamId: me?.teamId || null,
                        tableId: me?.tableId || null,
                        displayName: playerName, answer: m, kind: 'mood',
                      });
                      try {
                        await addDoc(collection(db, 'crowdmood'), {
                          userId: currentUser.uid,
                          clientId: channelName,
                          mood: m,
                          timestamp: serverTimestamp(),
                          date: new Date().toISOString().split('T')[0],
                        });
                      } catch (_) {}
                      awardPoints(nightId, {
                        uid: currentUser.uid, teamId: me?.teamId || null,
                        displayName: playerName,
                        action: 'mood', points: POINTS.mood,
                        ledgerId: `mood_${roundId}_${currentUser.uid}`,
                      }).catch(() => {});
                      setSent(true);
                      flash(`Mood sent — +${POINTS.mood}!`);
                    }}
                    disabled={!joined}
                    className="text-base border border-white/20 hover:border-purple-400/60 hover:bg-purple-500/10 px-5 py-3 rounded-xl transition disabled:opacity-40"
                  >
                    {m}
                  </button>
                ))}
              </div>
            )
          )}

          {/* Showdown final — Lyric Sudden Death is a 3-way multiple choice */}
          {/* Showdown Final — KARAOKE format (2026-08-20). Legacy lyricdeath
              multiple-choice stays below for older consoles. */}
          {round.type === 'showdown' && round.q?.type === 'showdownfinal' && (
            round.q.status === 'revealed' ? (
              <div className="text-base py-1">
                <div className="text-amber-300 font-bold mb-1">👑 {round.q.champion?.name} is tonight's karaoke champion!</div>
                <span className="text-white/50">+{POINTS.showdownWin} points — what a finale.</span>
              </div>
            ) : round.q.status === 'prep' ? (
              <div className="text-base py-2">
                <div className="text-white/80 mb-1">🎤 Karaoke tracks are being loaded — singers, get ready to perform!</div>
                {(round.q.karaoke?.songs || []).map((s, i) => (
                  <div key={i} className="text-white/60">
                    {i + 1}. <b>{s.title}</b> — 🎤 {s.singer.name}
                    {s.singer.uid === currentUser?.uid ? <span className="text-amber-300 font-bold"> (that's YOU — warm up!)</span> : null}
                  </div>
                ))}
              </div>
            ) : round.q.status === 'karaoke' ? (
              (() => {
                const song = round.q.karaoke?.songs?.[round.q.karaoke?.current];
                if (!song) return <p className="text-base text-white/50">Karaoke starting…</p>;
                return song.singer.uid === currentUser?.uid ? (
                  <div className="text-base py-2">
                    <div className="text-amber-300 font-bold text-lg">🎤 YOU'RE UP!</div>
                    <div className="text-white/80 mt-1">Grab the mic — <b>{song.title}</b> is playing and the lyrics are on the big screen.</div>
                  </div>
                ) : (
                  <div className="text-base py-2">
                    <div className="text-white/80">🎤 <b>{song.singer.name}</b> is singing <b>{song.title}</b> — cheer them on!</div>
                    <p className="text-white/50 mt-1">The best-singer vote comes after the last song…</p>
                  </div>
                );
              })()
            ) : round.q.status === 'singervote' ? (
              <>
                <div className="text-base text-white/80 mb-2">
                  🗳 <b>Who owned the mic?</b>
                  <span className="text-white/50"> Vote the best singer — champion takes +{POINTS.showdownWin}.</span>
                </div>
                <div className="space-y-1.5">
                  {(round.q.karaoke?.songs || []).map((s) => {
                    const isMe = s.singer.uid === currentUser?.uid;
                    const voted = mySingerVote && mySingerVote.svAtMs === round.q.svAtMs && mySingerVote.uid === s.singer.uid;
                    return (
                      <button key={s.singer.uid} onClick={() => voteSinger(s.singer)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition text-left ${
                          voted ? 'border-green-400/70 bg-green-500/10'
                            : isMe ? 'border-white/10 bg-white/[0.03] opacity-60'
                              : 'border-white/15 hover:border-pink-400/60 hover:bg-pink-500/10'}`}>
                        <span className="flex-1 min-w-0 text-base truncate">
                          🎤 <b>{s.singer.name}</b><span className="text-white/50"> — sang {s.title}</span>
                        </span>
                        <span className={`text-base flex-shrink-0 ${voted ? 'text-green-400' : isMe ? 'text-white/40' : 'text-pink-300'}`}>
                          {voted ? '✓ your vote' : isMe ? 'that’s you 🤭' : 'Vote'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <p className="text-base text-white/50">The final is being set up — hold on…</p>
            )
          )}

          {round.type === 'showdown' && round.q?.type !== 'showdownfinal' && (
            round.q?.type === 'lyricdeath' ? (
              round.q.status === 'revealed' ? (
                <div className="text-base py-1">
                  {myPick && (
                    myPick.index === round.q.correctIndex
                      ? <div className="text-green-400 font-bold mb-1">✓ You got it — +{POINTS.correctAnswer}!</div>
                      : <div className="text-red-400 font-bold mb-1">✗ Not this time — you're out of this one.</div>
                  )}
                  <span className="text-white/50">The missing line: </span>
                  <b className="text-green-400">{round.q.answer}</b>
                </div>
              ) : sent ? (
                <div className="text-base py-2">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle2 size={18} /> Locked in: <b>{myPick?.title}</b>
                  </div>
                  <button onClick={() => setSent(false)}
                    className="text-base text-white/50 underline mt-1.5">
                    Change answer
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-base text-white/70 mb-2">
                    🎤 The music stopped… what's the missing line?
                  </div>
                  {round.q.prompt && (
                    <div className="text-base italic text-white/80 mb-3 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
                      “{round.q.prompt}…”
                    </div>
                  )}
                  <div className="space-y-2">
                    {(round.q.choices || []).map((c, i) => (
                      <button key={i} onClick={() => pickChoice(i)}
                        disabled={!joined || round.status === 'scoring'}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-white/20 hover:border-red-400/60 hover:bg-red-500/10 transition text-left disabled:opacity-40">
                        <span className="font-mono font-bold text-base text-white/50">{['A', 'B', 'C'][i]}</span>
                        <span className="flex-1 min-w-0 text-base">{c}</span>
                      </button>
                    ))}
                  </div>
                </>
              )
            ) : (
              <p className="text-base text-white/60">The Showdown Final is on — eyes on the big screen.</p>
            )
          )}

          {round.type === 'wrap' && (
            <p className="text-base text-white/60">Season standings on screen. Prizes incoming!</p>
          )}
        </div>
      )}

      {players.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={17} className="text-amber-400" />
            <span className="text-base font-semibold">Tonight's leaderboard</span>
          </div>
          <div className="space-y-1.5">
            {players.slice(0, 8).map((p, i) => (
              <div key={p.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                p.uid === currentUser?.uid ? 'border-purple-500/50 bg-purple-500/10' : 'border-white/10 bg-white/5'}`}>
                <span className="font-mono text-base text-white/40 w-6">{i + 1}</span>
                <span className="flex-1 min-w-0 text-base truncate">
                  {p.uid === currentUser?.uid
                    ? `${p.nickname || playerName} (you)`
                    : (p.nickname || p.displayName || 'Guest')}
                </span>
                <span className="text-base font-mono font-bold">{p.points || 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveNightPanel;
